import {Engine} from "platform/engine/Engine";
import Platform from "platform/Platform";
import Utils from "platform/util/Utils";
import {IPage} from "kbd/pages/IPage";
import {PageNavigator} from "kbd/pages/PageNavigator";
import CoreEngine from "platform/engine/CoreEngine";
import {PageType} from "kbd/enum/PageType";
import {LoadLanguagePayload, SetSupportedLanguages} from "platform/redux/translation/TranslationActions";
import {Configuration} from "kbd/core/configuration/Configuration";
import {InitChat, NavigateToPayload, SetAppReadyPayload, SetUrls} from "platform/redux/core/CoreActions";
import {UrlType} from "platform/enum/UrlType";
import {HttpReject} from "platform/network/http/Http";
import {AvailableLanguage, LoginConfigurationResponse} from "kbd/protocol/response/LoginConfigurationResponse";
import {LangCode} from "platform/enum/LangCode";
import {XhrManager} from "kbd/core/engine/XhrManager";
import {AppState} from "kbd/core/state/AppState";
import {ServiceType} from "kbd/enum/ServiceType";
import {LoginConfigurationRequest} from "kbd/protocol/request/LoginConfigurationRequest";
import {ProductType} from "kbd/entry/ProductType";
import {TokenManager} from "kbd/core/util/TokenManager";
import WebUtil from "platform/util/WebUtil";
import {SetComplianceFlow, SetModal, SetPlatformType} from "kbd/core/redux/app/AppReduxActions";
import {RNBridge} from "kbd/core/integration/RNBridge";
import {ModalType} from "kbd/enum/ModalType";

const PlatformType: string = WebUtil.urlParam("platform");
const BI: string = WebUtil.urlParam("bi");

export default class AppEngine extends Engine {

    private static _instance: AppEngine;

    public static instance(): AppEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        CoreEngine.instance().setup().catch(() => {});
        Platform.dispatch(SetPlatformType({platformType: PlatformType}));
        const mixpanelApiKey: string = Platform.config().mixpanelApiKey;
        if (mixpanelApiKey && BI === "true") {
            Platform.bi().init(mixpanelApiKey);
        }
    }

    public onAppReady = ({ready}: SetAppReadyPayload): void => {
        if (ready) {
            const msg: string = JSON.stringify({operation: "appReady"});
            RNBridge.send(msg);
            if (WebUtil.parentWindow()) {
                WebUtil.parentWindow().postMessage(msg, "*");
            }
        }
    }

    public onLoadLanguage = async (payload: LoadLanguagePayload) => {
        const BrandId: number = Platform.config<Configuration>().brandId;
        this._logger.debug("Fetching config for lang: " + payload.langCode);
        const request: LoginConfigurationRequest = {BrandId, LanguageCode: payload.langCode};
        const answer: [HttpReject, LoginConfigurationResponse] = await Utils.to(XhrManager.sendToLogin(request, "LoginConfiguration"));
        if (answer[0]) {
            this._logger.debug("Failed fetch configuration: " + answer[0]);
        } else {
            const response: LoginConfigurationResponse = answer[1];
            if (response.SuccessStatus) {
                this._logger.debug("Config received. Should redirect to old login " + response.ShouldRediredtToOldLogin);
                const appState: AppState = Platform.state(ServiceType.App);
                if (appState.product === ProductType.Board) {
                    Platform.dispatch(SetComplianceFlow({complianceFlowType: response.ComplianceFlowType}));
                    if (response.NewLoginUrl) {
                        const GetDomain = (url: string): string => {
                            const parts: string [] = url?.split(".");
                            return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
                        };
                        const domain: string = GetDomain(window?.location?.hostname);
                        const newDomain: string = GetDomain(WebUtil.domainUrl(response.NewLoginUrl));
                        if (domain !== newDomain) {
                            Platform.dispatch(SetModal({
                                modalType: ModalType.LoginNewPlatform,
                                visible: true
                            }));
                        }
                    }
                }
                if (appState.product === ProductType.Board && response.ShouldRediredtToOldLogin) {
                    Platform.environment().redirect(response.OldLoginUrl);
                } else {
                    const token: string = await TokenManager.token();
                    if (appState.product !== ProductType.Board && Utils.isEmpty(token)) {
                        this._logger.debug("Token is NULL. Redirect to login");
                        Platform.environment().redirect(response.NewLoginUrl);
                    } else {
                        Platform.dispatch(SetUrls({
                            urls: [
                                {type: UrlType.SignUp, url: decodeURIComponent(response.SignUpUrl)},
                                {type: UrlType.NewLogin, url: decodeURIComponent(response.NewLoginUrl)},
                                {type: UrlType.FacebookLogin, url: decodeURIComponent(response.FacebookUrl)},
                                {type: UrlType.GoogleLogin, url: decodeURIComponent(response.GoogleUrl)}
                            ]
                        }));
                        const languages: LangCode[] = [];
                        response.AvailableLanguageConfigurations.forEach((langConfig: AvailableLanguage) => {
                            const langCode: LangCode = LangCode[langConfig.Code.toUpperCase()];
                            if (Utils.isNotNull(langCode) && langConfig.IsActive && Platform.config<Configuration>().supportedLanguages.indexOf(langCode) > -1) {
                                appState.addLanguage(langCode, langConfig);
                                languages.push(langCode);
                            }
                        });
                        Platform.dispatch(SetSupportedLanguages({languages}));
                        if (response.ChatUrl && WebUtil.isUrl(response.ChatUrl) && (appState.product === ProductType.Board || appState.product === ProductType.Kyc)) {
                            Platform.dispatch(InitChat({scriptUrl: response.ChatUrl}));
                        } else {
                            this._logger.warn("Z-chat url not configured");
                        }
                    }
                }
            } else {
                this._logger.debug("Failed fetch config: " + response.LocalizedErrorMessage);
            }
        }
    }

    public closeMe = (): void => {
        this._logger.debug("Asking to cloe me");
        if (WebUtil.inIosWebView()) {
            (window as any).webkit.messageHandlers.jsHandle.postMessage("closeWebView");
        } else if (WebUtil.inAndroidWebView()) {
            (window as any).MobileJsInterface.closeWebView();
        } else if (WebUtil.inRNWebView()) {
            RNBridge.send(JSON.stringify({operation: "closeGeneralWindow"}));
        } else if (WebUtil.parentWindow()) {
            WebUtil.parentWindow().postMessage(JSON.stringify({operation: "closeGeneralWindow"}), "*");
        }
    }

    public setNextPage = (payload: NavigateToPayload): void => {
        const nextPage: IPage = PageNavigator.next(payload.route as PageType);
        if (Utils.isNotNull(nextPage)) {
            this._logger.debug("Set next page to: " + nextPage.type);
            Platform.router().navigate(nextPage.type, payload.params);
        } else {
            this._logger.warn("Absent next page after: " + payload.route);
        }
    }

    public setPrevPage = (payload: NavigateToPayload): void => {
        const prevPage: IPage = PageNavigator.prev(payload.route as PageType);
        if (Utils.isNotNull(prevPage)) {
            this._logger.debug("Set prev page to: " + prevPage.type);
            Platform.router().navigate(prevPage.type, payload.params);
        } else {
            this._logger.warn("Absent before page related to current: " + payload.route);
        }
    }
}
